import { Card, Loader, SectionMessage } from "@certn/cascadia-react";
import { Stack, Text } from "@certn/cascadia";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useClientPortalAPI, APIError } from "@certn/api";
import { AdjudicationCheckCard } from "../components/AdjudicationCheckCard";
import { components } from "@certn/api/generated-schemas/client-portal";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useUser } from "../../../hooks/useUser";
import { EmailVerificationBanner } from "../../dashboard/EmailVerificationBanner";

interface Props {
  caseId: string | undefined;
}

export function AdjudicationTab({ caseId }: Props) {
  const { t } = useTranslation();
  const { details: userDetails } = useUser();
  const { clientPortalRequireEmailVerification } = useFlags();
  const {
    data: adjudicationData,
    isLoading,
    error,
  } = useClientPortalAPI(
    `/api/internal/client-portal/adjudication-rubrics/case-results/${caseId}/` as "/api/internal/client-portal/adjudication-rubrics/case-results/{case_short_id}/"
  );

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    const errorMessage =
      error instanceof APIError
        ? error.detail
        : t("Unable to load adjudication results for this case.");

    return (
      <SectionMessage
        type="danger"
        icon="error"
        sectionTitle={t("Error loading adjudication results")}
      >
        {errorMessage}
      </SectionMessage>
    );
  }

  if (
    clientPortalRequireEmailVerification &&
    userDetails?.auth0_email_verified === false
  ) {
    return (
      <Card
        className={styles.cardContainer}
        type="large"
        responsive
        testID="adjudication"
        border="border-base"
      >
        <Text style="global/heading-large">{t("Adjudication results")}</Text>
        <EmailVerificationBanner
          type="danger"
          title={t("Please verify your email to view your case results.")}
          message={t(
            "Until your email is verified, your case results will remain hidden."
          )}
        />
      </Card>
    );
  }

  return (
    <Card
      className={styles.cardContainer}
      type="large"
      responsive
      testID="adjudication"
      border="border-base"
    >
      <Text style="global/heading-large">{t("Adjudication results")}</Text>
      <Stack gap="space-10" orientation="horizontal">
        <Text style="global/heading-medium">{t("Rubric name")}</Text>
        <Text style="global/body-primary" color="text-weak">
          {adjudicationData?.rubric_name ?? "--"}
        </Text>
      </Stack>
      <Stack gap="space-4">
        {adjudicationData?.checks.map(
          (check: components["schemas"]["AdjudicationCaseResultsCheck"]) => {
            return <AdjudicationCheckCard key={check.id} check={check} />;
          }
        )}
      </Stack>
    </Card>
  );
}

const styles = {
  cardContainer: css({
    padding: "var(--space-5) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
};
